/**
 * React Router 6 — createBrowserRouter wiring the three layout groups.
 *
 * Layout shells are eagerly imported (small, always needed).
 * Top-level pages use either:
 *   - React.lazy + <Suspense> (legacy public/portal pages — unchanged)
 *   - RR6 route-level `lazy` property (all (app) pages added in M1.4+)
 *     Each module must export a default component; RR6 wraps it automatically.
 *
 * RequireAuth wraps the (app) and (portal) groups.
 */

import { lazy, Suspense, type ComponentType, type ReactNode } from 'react';
import { createBrowserRouter, Navigate } from 'react-router-dom';

import PublicLayout from '@/layouts/PublicLayout';
import AppLayout from '@/layouts/AppLayout';
import PortalLayout from '@/layouts/PortalLayout';
import RequireAuth from '@/components/auth/RequireAuth';
import RouteError from '@/components/RouteError';

// ─── Legacy React.lazy imports (public + portal — unchanged) ──────────────────

// (public)
const LoginPage = lazy(() => import('@/routes/(public)/login'));
const RegisterPage = lazy(() => import('@/routes/(public)/register'));

// (portal)
const PortalHomePage = lazy(() => import('@/routes/(portal)/index'));

// ─── Loading fallback (used by legacy Suspense wrappers) ──────────────────────

function PageLoader() {
  return (
    <div className="flex items-center justify-center h-full min-h-32">
      <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
    </div>
  );
}

function Lazy({ children }: { children: ReactNode }) {
  return <Suspense fallback={<PageLoader />}>{children}</Suspense>;
}

// ─── RR6 route-level lazy helper ─────────────────────────────────────────────
// RR6's route `lazy` property expects the async function to return a partial
// route object. It does NOT automatically pick up `default` exports — we must
// map { default } → { Component }.
//
// Usage in the route tree:
//   lazy: rr6Lazy(() => import('@/routes/(app)/some-page'))
//
// This keeps page modules using the standard `export default function Page()`.

interface PageModule { default: ComponentType }

function rr6Lazy(importer: () => Promise<PageModule>) {
  return async () => {
    const mod = await importer();
    return { Component: mod.default };
  };
}

// ─── Router ───────────────────────────────────────────────────────────────────

export const router = createBrowserRouter([
  // ── Root redirect ───────────────────────────────────────────────────────
  {
    index: true,
    path: '/',
    element: <Navigate to="/dashboard" replace />,
  },

  // ── Public self-registration (wide, standalone — not the narrow card) ────
  {
    path: 'register',
    handle: { title: 'Register' },
    element: (
      <Lazy>
        <RegisterPage />
      </Lazy>
    ),
  },

  // ── Public layout (login) ─────────────────────────────────────────────────
  // forgot-password and reset-password routes have been retired; password
  // resets are admin-initiated (POST /users/{id}/reset-password-email).
  {
    element: <PublicLayout />,
    children: [
      {
        path: 'login',
        handle: { title: 'Sign in' },
        element: (
          <Lazy>
            <LoginPage />
          </Lazy>
        ),
      },
    ],
  },

  // ── App layout (authenticated internal staff) ───────────────────────────
  {
    element: <RequireAuth />,
    children: [
      {
        element: <AppLayout />,
        // Catches lazy-chunk load failures + render/loader errors for any app page.
        errorElement: <RouteError />,
        children: [
          // Dashboard (M1.5 — role-dispatching smart view)
          {
            path: 'dashboard',
            handle: { title: 'Dashboard', crumb: 'Dashboard' },
            lazy: rr6Lazy(() => import('@/routes/(app)/dashboard/index')),
          },

          // My Task Manager (UX redesign — unified batch + invoice workflow)
          {
            path: 'tasks',
            handle: { title: 'My Task Manager', crumb: 'My Tasks' },
            lazy: rr6Lazy(() => import('@/routes/(app)/tasks/index')),
          },

          // ── Help & Guides ──────────────────────────────────────────────
          {
            path: 'help',
            handle: { title: 'Help & Guides', crumb: 'Help' },
            lazy: rr6Lazy(() => import('@/routes/(app)/help/index')),
          },
          {
            path: 'help/:section',
            handle: { title: 'Help', crumb: 'Help' },
            lazy: rr6Lazy(() => import('@/routes/(app)/help/section')),
          },
          {
            path: 'help/:section/:page',
            handle: { title: 'Help', crumb: 'Help' },
            lazy: rr6Lazy(() => import('@/routes/(app)/help/section')),
          },

          // ── Parties ────────────────────────────────────────────────────
          {
            path: 'parties',
            handle: { title: 'Parties', crumb: 'Parties' },
            lazy: rr6Lazy(() => import('@/routes/(app)/parties/list')),
          },
          {
            path: 'parties/new',
            handle: { title: 'New Party', crumb: 'New Party' },
            lazy: rr6Lazy(() => import('@/routes/(app)/parties/new')),
          },
          {
            path: 'parties/:partyId',
            handle: {
              title: 'Party Detail',
              crumb: ({ params }: { params: Record<string, string | undefined> }) =>
                params.partyId?.slice(-6) ?? 'Detail',
            },
            lazy: rr6Lazy(() => import('@/routes/(app)/parties/detail')),
          },

          // ── Registration requests (admin) ──────────────────────────────
          {
            path: 'registrations',
            handle: { title: 'Registration Requests', crumb: 'Registrations' },
            lazy: rr6Lazy(() => import('@/routes/(app)/registrations/index')),
          },

          // ── Change password (self-service, all authenticated users) ──────
          {
            path: 'settings/password',
            handle: { title: 'Change Password', crumb: 'Change Password' },
            lazy: rr6Lazy(() => import('@/routes/(app)/settings/change-password')),
          },

          // ── Email settings (admin) ─────────────────────────────────────
          {
            path: 'settings/email',
            handle: { title: 'Email Settings', crumb: 'Email Settings' },
            lazy: rr6Lazy(() => import('@/routes/(app)/settings/email')),
          },

          // ── Tally connector settings (admin) ──────────────────────────
          {
            path: 'settings/tally-connector',
            handle: { title: 'Tally Connector', crumb: 'Tally Connector' },
            lazy: rr6Lazy(() => import('@/routes/(app)/settings/tally-connector')),
          },

          // ── AI invoice extractor settings (admin) ─────────────────────
          {
            path: 'settings/invoice-extractor',
            handle: { title: 'AI Invoice Extractor', crumb: 'AI Invoice Extractor' },
            lazy: rr6Lazy(() => import('@/routes/(app)/settings/invoice-extractor')),
          },

          // ── Registration approvers (admin) — dynamic dual-approval list ─
          {
            path: 'settings/registration-approvers',
            handle: { title: 'Registration Approvers', crumb: 'Registration Approvers' },
            lazy: rr6Lazy(() => import('@/routes/(app)/settings/registration-approvers')),
          },

          // ── Users (admin) ──────────────────────────────────────────────
          {
            path: 'users',
            handle: { title: 'Users', crumb: 'Users' },
            lazy: rr6Lazy(() => import('@/routes/(app)/users/list')),
          },
          {
            path: 'users/new',
            handle: { title: 'New User', crumb: 'New User' },
            lazy: rr6Lazy(() => import('@/routes/(app)/users/new')),
          },
          {
            path: 'users/faculty-import',
            handle: { title: 'Import Faculty', crumb: 'Import Faculty' },
            lazy: rr6Lazy(() => import('@/routes/(app)/users/faculty-import')),
          },
          {
            path: 'users/:userId',
            handle: {
              title: 'User Detail',
              crumb: ({ params }: { params: Record<string, string | undefined> }) =>
                params.userId?.slice(-6) ?? 'Detail',
            },
            lazy: rr6Lazy(() => import('@/routes/(app)/users/detail')),
          },

          // ── Leave: self-service (L1–L3), approvals (L4/L5), admin manage ─
          {
            path: 'my-leave',
            handle: { title: 'My Leave', crumb: 'My Leave' },
            lazy: rr6Lazy(() => import('@/routes/(app)/leave/my-leave')),
          },
          {
            path: 'leave-approvals',
            handle: { title: 'Leave Approvals', crumb: 'Leave Approvals' },
            lazy: rr6Lazy(() => import('@/routes/(app)/leave/approvals')),
          },
          {
            path: 'leave',
            handle: { title: 'Leave Management', crumb: 'Leave' },
            lazy: rr6Lazy(() => import('@/routes/(app)/leave/index')),
          },

          // ── Roles (admin) ──────────────────────────────────────────────
          {
            path: 'roles',
            handle: { title: 'Roles', crumb: 'Roles' },
            lazy: rr6Lazy(() => import('@/routes/(app)/roles/list')),
          },
          {
            path: 'roles/new',
            handle: { title: 'New Role', crumb: 'New Role' },
            lazy: rr6Lazy(() => import('@/routes/(app)/roles/new')),
          },
          {
            path: 'roles/:roleId',
            handle: {
              title: 'Role Detail',
              crumb: ({ params }: { params: Record<string, string | undefined> }) =>
                params.roleId?.slice(-6) ?? 'Detail',
            },
            lazy: rr6Lazy(() => import('@/routes/(app)/roles/detail')),
          },

          // ── Batches ────────────────────────────────────────────────────
          {
            path: 'batches',
            handle: { title: 'Batches', crumb: 'Batches' },
            lazy: rr6Lazy(() => import('@/routes/(app)/batches/list')),
          },
          {
            path: 'batches/new',
            handle: { title: 'New Batch', crumb: 'New Batch' },
            lazy: rr6Lazy(() => import('@/routes/(app)/batches/new')),
          },
          {
            path: 'batches/:batchId',
            handle: {
              title: 'Batch Detail',
              crumb: ({ params }: { params: Record<string, string | undefined> }) =>
                params.batchId?.slice(-6) ?? 'Detail',
            },
            lazy: rr6Lazy(() => import('@/routes/(app)/batches/detail')),
          },
          {
            path: 'batches/:batchId/attendance',
            handle: {
              title: 'Batch Attendance',
              crumb: 'Attendance',
            },
            lazy: rr6Lazy(() => import('@/routes/(app)/batches/attendance')),
          },

          // ── Invoices ────────────────────────────────────────────────────
          {
            path: 'invoices',
            handle: { title: 'Invoices', crumb: 'Invoices' },
            lazy: rr6Lazy(() => import('@/routes/(app)/invoices/list')),
          },
          {
            path: 'invoices/:invoiceId',
            handle: {
              title: 'Invoice Detail',
              crumb: ({ params }: { params: Record<string, string | undefined> }) =>
                params.invoiceId?.slice(-6) ?? 'Detail',
            },
            lazy: rr6Lazy(() => import('@/routes/(app)/invoices/detail')),
          },

          // ── Approvals ──────────────────────────────────────────────────
          {
            path: 'approvals',
            handle: { title: 'My Pending Approvals', crumb: 'Approvals' },
            lazy: rr6Lazy(() => import('@/routes/(app)/approvals/index')),
          },
          {
            path: 'approvals/pending-bos',
            handle: { title: 'Pending BoS Receipt', crumb: 'Pending BoS' },
            lazy: rr6Lazy(() => import('@/routes/(app)/approvals/pending-bos')),
          },
          {
            path: 'approvals/bypass',
            handle: { title: 'Bypass Approvals', crumb: 'Bypass' },
            lazy: rr6Lazy(() => import('@/routes/(app)/approvals/bypass')),
          },

          // ── Receipts ───────────────────────────────────────────────────────
          {
            path: 'receipts',
            handle: { title: 'BoS Receipts', crumb: 'Receipts' },
            lazy: rr6Lazy(() => import('@/routes/(app)/receipts/list')),
          },
          {
            path: 'receipts/new',
            handle: { title: 'New Receipt', crumb: 'New Receipt' },
            lazy: rr6Lazy(() => import('@/routes/(app)/receipts/new')),
          },
          // NOTE: receipts/import MUST be declared before receipts/:receiptId so the
          // literal segment "import" is never matched as a receiptId parameter.
          {
            path: 'receipts/import',
            handle: { title: 'Import Receipts', crumb: 'Import' },
            lazy: rr6Lazy(() => import('@/routes/(app)/receipts/import')),
          },
          {
            path: 'receipts/:receiptId',
            handle: {
              title: 'Receipt Detail',
              crumb: ({ params }: { params: Record<string, string | undefined> }) =>
                params.receiptId?.slice(-6) ?? 'Detail',
            },
            lazy: rr6Lazy(() => import('@/routes/(app)/receipts/detail')),
          },

          // ── Payment Vouchers (BPV) ──────────────────────────────────────
          {
            path: 'bpv',
            handle: { title: 'Payment Vouchers', crumb: 'BPVs' },
            lazy: rr6Lazy(() => import('@/routes/(app)/bpv/list')),
          },
          {
            path: 'bpv/:bpvId',
            handle: {
              title: 'BPV Detail',
              crumb: ({ params }: { params: Record<string, string | undefined> }) =>
                params.bpvId?.slice(-6) ?? 'Detail',
            },
            lazy: rr6Lazy(() => import('@/routes/(app)/bpv/detail')),
          },

          // ── GST Compliance ─────────────────────────────────────────────
          {
            path: 'gst/holds',
            handle: { title: 'GST Holds', crumb: 'GST Holds' },
            lazy: rr6Lazy(() => import('@/routes/(app)/gst/holds')),
          },
          {
            path: 'gst/2b-uploads',
            handle: { title: 'GSTR-2B Uploads', crumb: '2B Uploads' },
            lazy: rr6Lazy(() => import('@/routes/(app)/gst/uploads')),
          },
          {
            path: 'gst/2b-uploads/:uploadId',
            handle: { title: 'GSTR-2B Upload Detail', crumb: 'Detail' },
            lazy: rr6Lazy(() => import('@/routes/(app)/gst/upload-detail')),
          },
          {
            path: 'gst/2b-automated',
            handle: { title: 'GSTR-2B Automated Download', crumb: '2B Automated' },
            lazy: rr6Lazy(() => import('@/routes/(app)/gst/automated')),
          },
          {
            path: 'gst/non-compliance-report',
            handle: { title: 'GST Non-Compliance Report', crumb: 'Non-Compliance' },
            lazy: rr6Lazy(() => import('@/routes/(app)/gst/report')),
          },
          {
            path: 'gst/pending-payments',
            handle: { title: 'Pending GST Payments', crumb: 'Pending Payments' },
            lazy: rr6Lazy(() => import('@/routes/(app)/gst/pending-payments')),
          },

          // ── Checks (M4.2) ──────────────────────────────────────────────
          // NOTE: checks/new MUST be declared before checks/:checkId so the
          // literal segment "new" is never matched as a checkId parameter.
          {
            path: 'checks',
            handle: { title: 'Checks', crumb: 'Checks' },
            lazy: rr6Lazy(() => import('@/routes/(app)/checks/list')),
          },
          {
            path: 'checks/new',
            handle: { title: 'New Check', crumb: 'New Check' },
            lazy: rr6Lazy(() => import('@/routes/(app)/checks/new')),
          },
          {
            path: 'checks/:checkId',
            handle: {
              title: ({ params }: { params: Record<string, string | undefined> }) =>
                `Check ${params.checkId?.slice(-6) ?? ''}`,
              crumb: 'Detail',
            },
            lazy: rr6Lazy(() => import('@/routes/(app)/checks/detail')),
          },

          // ── Bank Templates (M4.2, admin-only) ──────────────────────────
          // NOTE: bank-templates/new MUST be declared before bank-templates/:templateId.
          {
            path: 'bank-templates',
            handle: { title: 'Bank Templates', crumb: 'Bank Templates' },
            lazy: rr6Lazy(() => import('@/routes/(app)/bank-templates/list')),
          },
          {
            path: 'bank-templates/new',
            handle: { title: 'New Bank Template', crumb: 'New Template' },
            lazy: rr6Lazy(() => import('@/routes/(app)/bank-templates/new')),
          },
          {
            path: 'bank-templates/:templateId',
            handle: {
              title: ({ params }: { params: Record<string, string | undefined> }) =>
                `Bank Template ${params.templateId?.slice(-6) ?? ''}`,
              crumb: 'Edit',
            },
            lazy: rr6Lazy(() => import('@/routes/(app)/bank-templates/detail')),
          },

          // ── Reports (M4.3) ─────────────────────────────────────────
          {
            path: 'reports',
            handle: { title: 'Reports', crumb: 'Reports' },
            lazy: rr6Lazy(() => import('@/routes/(app)/reports/index')),
          },
          {
            path: 'reports/tds',
            handle: { title: 'TDS Report', crumb: 'TDS' },
            lazy: rr6Lazy(() => import('@/routes/(app)/reports/tds')),
          },
          {
            path: 'reports/payment-reconciliation',
            handle: {
              title: 'Payment Reconciliation',
              crumb: 'Payment Reconciliation',
            },
            lazy: rr6Lazy(
              () => import('@/routes/(app)/reports/payment-reconciliation')
            ),
          },
          {
            path: 'reports/faculty-earnings',
            handle: { title: 'Faculty Earnings', crumb: 'Faculty Earnings' },
            lazy: rr6Lazy(() => import('@/routes/(app)/reports/faculty-earnings')),
          },
          {
            path: 'reports/batch-pnl',
            handle: { title: 'Batch P&L', crumb: 'Batch P&L' },
            lazy: rr6Lazy(() => import('@/routes/(app)/reports/batch-pnl')),
          },
          {
            path: 'reports/batch-payment-summary',
            handle: {
              title: 'Final Batch Payment Summary',
              crumb: 'Batch Payment Summary',
            },
            lazy: rr6Lazy(() => import('@/routes/(app)/reports/batch-payment-summary')),
          },

          // ── Notifications (M4.4) ───────────────────────────────────
          {
            path: 'notifications',
            handle: { title: 'Notifications', crumb: 'Notifications' },
            lazy: rr6Lazy(() => import('@/routes/(app)/notifications/list')),
          },
          {
            path: 'notifications/preferences',
            handle: { title: 'Notification Preferences', crumb: 'Preferences' },
            lazy: rr6Lazy(() => import('@/routes/(app)/notifications/preferences')),
          },

          // ── Email Logs (M4.4) ──────────────────────────────────────
          {
            path: 'email-logs',
            handle: { title: 'Email Logs', crumb: 'Email Logs' },
            lazy: rr6Lazy(() => import('@/routes/(app)/email-logs/list')),
          },
          {
            path: 'email-logs/:logId',
            handle: {
              title: 'Email Log Detail',
              crumb: ({ params }: { params: Record<string, string | undefined> }) =>
                params.logId?.slice(-6) ?? 'Detail',
            },
            lazy: rr6Lazy(() => import('@/routes/(app)/email-logs/detail')),
          },

          // ── Email Templates (M4.4, admin-only) ────────────────────
          {
            path: 'email-templates',
            handle: { title: 'Email Templates', crumb: 'Email Templates' },
            lazy: rr6Lazy(() => import('@/routes/(app)/email-templates/list')),
          },
          {
            path: 'email-templates/:templateId',
            handle: {
              title: 'Edit Email Template',
              crumb: ({ params }: { params: Record<string, string | undefined> }) =>
                params.templateId?.slice(-6) ?? 'Detail',
            },
            lazy: rr6Lazy(() => import('@/routes/(app)/email-templates/detail')),
          },

          // ── Faculty Fee Rates (admin master-data) ──────────────────
          {
            path: 'faculty-fee-rates',
            handle: { title: 'Faculty Fee Rates', crumb: 'Faculty Fee Rates' },
            lazy: rr6Lazy(() => import('@/routes/(app)/faculty-fee-rates/index')),
          },

          // ── Vendor Fee Rates (admin master-data) ───────────────────
          {
            path: 'vendor-fee-rates',
            handle: { title: 'Vendor Fee Rates', crumb: 'Vendor Fee Rates' },
            lazy: rr6Lazy(() => import('@/routes/(app)/vendor-fee-rates/index')),
          },

          // ── Tally Vouchers (sync ledgers + post journal/payment) ────
          {
            path: 'tally-vouchers',
            handle: { title: 'Tally Vouchers', crumb: 'Tally Vouchers' },
            lazy: rr6Lazy(() => import('@/routes/(app)/tally-vouchers/index')),
          },

          // ── Archived Batches (admin only) ──────────────────────────
          {
            path: 'archived-batches',
            handle: { title: 'Archived Batches', crumb: 'Archived Batches' },
            lazy: rr6Lazy(() => import('@/routes/(app)/archived-batches/index')),
          },

          // ── Party Review (L5 Chairman oversight) ───────────────────
          {
            path: 'party-review',
            handle: { title: 'Party Review', crumb: 'Party Review' },
            lazy: rr6Lazy(() => import('@/routes/(app)/party-review/index')),
          },

          // ── Audit Log (M4.5) ───────────────────────────────────────
          // Static path "audit-log" is declared before the dynamic ":logId"
          // segment to guarantee static-before-dynamic ordering.
          {
            path: 'audit-log',
            handle: { title: 'Audit Log', crumb: 'Audit Log' },
            lazy: rr6Lazy(() => import('@/routes/(app)/audit-log/list')),
          },
          {
            path: 'audit-log/:logId',
            handle: { title: 'Audit Entry', crumb: 'Entry' },
            lazy: rr6Lazy(() => import('@/routes/(app)/audit-log/detail')),
          },

          // Downstream agents add more routes here:
          // settings

          // ── Catch-all — MUST be last. Any URL that matches no route above
          // (typo / stale link / removed page) redirects to the dashboard.
          {
            path: '*',
            element: <Navigate to="/dashboard" replace />,
          },
        ],
      },
    ],
  },

  // ── Portal layout (external faculty/vendor) ─────────────────────────────
  {
    element: <RequireAuth />,
    children: [
      {
        element: <PortalLayout />,
        errorElement: <RouteError />,
        children: [
          {
            path: 'portal',
            handle: { title: 'Portal' },
            element: (
              <Lazy>
                <PortalHomePage />
              </Lazy>
            ),
          },

          // ── Portal invoice routes ─────────────────────────────────────
          {
            path: 'portal/invoices',
            handle: { title: 'My Invoices', crumb: 'My Invoices' },
            lazy: rr6Lazy(() => import('@/routes/(portal)/invoices/list')),
          },
          {
            path: 'portal/invoices/new',
            handle: { title: 'Submit Invoice', crumb: 'Submit Invoice' },
            lazy: rr6Lazy(() => import('@/routes/(portal)/invoices/new')),
          },
          {
            path: 'portal/invoices/:invoiceId',
            handle: {
              title: 'Invoice Detail',
              crumb: ({ params }: { params: Record<string, string | undefined> }) =>
                params.invoiceId?.slice(-6) ?? 'Detail',
            },
            lazy: rr6Lazy(() => import('@/routes/(portal)/invoices/detail')),
          },
          {
            path: 'portal/batches',
            handle: { title: 'My Batches', crumb: 'My Batches' },
            lazy: rr6Lazy(() => import('@/routes/(portal)/batches/index')),
          },
          {
            path: 'portal/attendance',
            handle: { title: 'My Attendance', crumb: 'My Attendance' },
            lazy: rr6Lazy(() => import('@/routes/(portal)/attendance/index')),
          },
          {
            path: 'portal/complete-registration',
            handle: { title: 'Complete Registration', crumb: 'Complete Registration' },
            lazy: rr6Lazy(() => import('@/routes/(portal)/complete-registration')),
          },
          {
            path: 'portal/profile',
            handle: { title: 'My Profile', crumb: 'My Profile' },
            lazy: rr6Lazy(() => import('@/routes/(portal)/profile')),
          },

          // ── Portal Help & Guides ──────────────────────────────────────────
          // Faculty/vendor-facing help. Reuses the shared markdown under
          // public/help/portal/ (also surfaced in the internal Help Center).
          {
            path: 'portal/help',
            handle: { title: 'Help & Guides', crumb: 'Help' },
            lazy: rr6Lazy(() => import('@/routes/(portal)/help/index')),
          },
          {
            path: 'portal/help/:page',
            handle: { title: 'Help & Guides', crumb: 'Help' },
            lazy: rr6Lazy(() => import('@/routes/(portal)/help/index')),
          },

          // ── Change password (portal) ──────────────────────────────────────
          // Same page as the internal /settings/password, but inside the portal
          // layout so external users with must_change_password=true can reach it
          // without RequireUserType bouncing them (fixes the redirect loop).
          {
            path: 'portal/password',
            handle: { title: 'Change Password', crumb: 'Change Password' },
            lazy: rr6Lazy(() => import('@/routes/(app)/settings/change-password')),
          },
        ],
      },
    ],
  },
]);
